import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { SectionCard } from "./SectionCard";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils/cn";
import { formatCompactNumber, formatCurrency } from "../utils";
import {
  useDailySpend,
  type DailySpendPoint,
  type OverTimeInterval,
  type StatsFilters,
} from "../api";

type Props = { filters: StatsFilters };

const KEYS = ["api_calls", "models", "flows", "rags", "deployments"] as const;
type SpendKey = (typeof KEYS)[number];

const KEY_LABEL_TOKENS: Record<SpendKey, string> = {
  api_calls: "usageAndCost.dailySpend.apis",
  models: "usageAndCost.dailySpend.models",
  flows: "usageAndCost.dailySpend.flows",
  rags: "usageAndCost.dailySpend.rags",
  deployments: "usageAndCost.dailySpend.deployments",
};

const KEY_COLORS: Record<SpendKey, string> = {
  api_calls: "#8b5cf6",
  models: "#fb8585",
  flows: "#45c2dd",
  rags: "#ffb052",
  deployments: "#5b8df4",
};

export function DailySpend({ filters }: Props) {
  const { t } = useTranslation();
  const [visibleKeys, setVisibleKeys] = React.useState<Set<SpendKey>>(
    () => new Set(KEYS),
  );
  const interval: OverTimeInterval =
    filters.endDate.getTime() - filters.startDate.getTime() <=
    48 * 60 * 60 * 1000
      ? "hour"
      : "day";
  const spend = useDailySpend(filters, interval);
  const data = spend.data?.data ?? [];
  const unavailableKeys = new Set(spend.data?.unavailable_categories ?? []);
  const availableKeys = KEYS.filter((key) => !unavailableKeys.has(key));

  const labelFor = React.useCallback(
    (key: string) => {
      if (key === "totalSpend") {
        return t("usageAndCost.dailySpend.totalSpend");
      }

      const token = KEY_LABEL_TOKENS[key as keyof typeof KEY_LABEL_TOKENS];
      return token ? t(token) : key;
    },
    [t],
  );

  const toggleKey = (key: SpendKey) => {
    setVisibleKeys((current) => {
      const next = new Set(current);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next.size ? next : current;
    });
  };

  const showAll = () => setVisibleKeys(new Set(availableKeys));

  const allVisible = availableKeys.every((key) => visibleKeys.has(key));

  const formatBucket = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    const options: Intl.DateTimeFormatOptions =
      interval === "hour"
        ? {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            timeZone: "UTC",
          }
        : {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            timeZone: "UTC",
          };
    return new Intl.DateTimeFormat(undefined, options).format(parsed);
  };

  return (
    <SectionCard
      title={t("usageAndCost.dailySpend.title")}
      subtitle={
        spend.data?.spend_complete === false
          ? t("usageAndCost.dailySpend.partialSpend")
          : undefined
      }
      category={t("usageAndCost.categories.all")}
      bodyClassName="pt-2"
    >
      <div className="min-h-[340px] w-full flex-1" dir="ltr">
        {spend.isLoading ? (
          <Skeleton className="h-full min-h-[340px] w-full" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              barCategoryGap="36%"
              margin={{ top: 42, right: 16, bottom: 0, left: 0 }}
            >
              <CartesianGrid
                stroke="var(--border)"
                opacity={0.65}
                vertical={false}
              />
              <XAxis
                dataKey={(point) => point.bucket_start ?? point.date}
                tick={{ fill: "var(--muted-foreground)", fontSize: 12 }}
                tickFormatter={(value: string) => formatBucket(value)}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: "var(--muted-foreground)", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) =>
                  `$${formatCompactNumber(Number(value))}`
                }
              />
              <Tooltip
                cursor={{
                  fill: "var(--primary)",
                  fillOpacity: 0.035,
                }}
                offset={16}
                wrapperStyle={{
                  outline: "none",
                  pointerEvents: "none",
                  zIndex: 10,
                }}
                content={(props) => (
                  <DailySpendTooltip {...props} labelFor={labelFor} />
                )}
              />
              <Legend
                verticalAlign="top"
                align="center"
                content={() => (
                  <DailySpendLegend
                    allLabel={t("usageAndCost.categories.all")}
                    allVisible={allVisible}
                    keys={availableKeys}
                    labelFor={labelFor}
                    onShowAll={showAll}
                    onToggleKey={toggleKey}
                    visibleKeys={visibleKeys}
                  />
                )}
              />
              {availableKeys
                .filter((key) => visibleKeys.has(key))
                .map((key) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    stackId="spend"
                    fill={KEY_COLORS[key]}
                  />
                ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </SectionCard>
  );
}

type LegendProps = {
  allLabel: string;
  allVisible: boolean;
  keys: SpendKey[];
  labelFor: (key: string) => string;
  onShowAll: () => void;
  onToggleKey: (key: SpendKey) => void;
  visibleKeys: Set<SpendKey>;
};

function DailySpendLegend({
  allLabel,
  allVisible,
  keys,
  labelFor,
  onShowAll,
  onToggleKey,
  visibleKeys,
}: LegendProps) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 pb-3 text-sm text-muted-foreground">
      <button
        type="button"
        onClick={onShowAll}
        className={cn(
          "rounded-full px-3 py-1 transition-colors",
          allVisible
            ? "bg-primary/20 text-foreground"
            : "hover:bg-muted hover:text-foreground",
        )}
      >
        {allLabel}
      </button>
      {keys.map((key) => {
        const active = visibleKeys.has(key);

        return (
          <button
            key={key}
            type="button"
            onClick={() => onToggleKey(key)}
            className={cn(
              "inline-flex items-center gap-2 transition-opacity hover:text-foreground",
              active ? "opacity-100" : "opacity-40",
            )}
          >
            <span
              className="size-3"
              style={{ backgroundColor: KEY_COLORS[key] }}
              aria-hidden
            />
            {labelFor(key)}
          </button>
        );
      })}
    </div>
  );
}

type TooltipPayload = {
  dataKey?: string | number;
  value?: unknown;
  payload?: DailySpendPoint;
};

type TooltipProps = {
  active?: boolean;
  label?: string;
  labelFor: (key: string) => string;
  payload?: TooltipPayload[];
};

function DailySpendTooltip({
  active,
  label,
  labelFor,
  payload = [],
}: TooltipProps) {
  if (!active || !payload.length) return null;

  const rows = payload.filter(
    (item): item is TooltipPayload & { dataKey: SpendKey; value: number } =>
      typeof item.dataKey === "string" &&
      KEYS.includes(item.dataKey as SpendKey) &&
      typeof item.value === "number",
  );
  const totalSpend = rows.reduce((sum, item) => sum + item.value, 0);
  const reportedTotal = payload[0]?.payload?.total_spend;

  return (
    <div className="min-w-[220px] rounded-md border border-border bg-popover p-3 text-xs text-muted-foreground shadow-xl">
      <p className="mb-3 text-base font-semibold text-popover-foreground">
        {label}
      </p>
      <p className="mb-2 text-sm font-medium text-primary">
        {labelFor("totalSpend")}: {formatCurrency(reportedTotal ?? totalSpend)}
      </p>
      <div className="space-y-2">
        {rows.map((item) => (
          <div
            key={item.dataKey}
            className="flex items-center justify-between gap-6"
          >
            <span className="inline-flex items-center gap-2">
              <span
                className="size-2.5"
                style={{ backgroundColor: KEY_COLORS[item.dataKey] }}
                aria-hidden
              />
              {labelFor(item.dataKey)}
            </span>
            <span>{formatCurrency(item.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
